import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../supabaseClient';

// ─────────────────────────────────────────────────────────────────────────────
// Knowledge Nuggets — review screen for Knowledge Capture
// (db_schema/knowledge_capture.sql)
//
// Nuggets arrive from extraction as raw_candidate. Only approved and
// expert_verified ones will ever feed the advice agent, so this page is the
// gate: approve, reject, fix the wording, or flip who may hear it.
//
// audience defaults to 'internal' in the table on purpose — a nugget only
// reaches customers once someone here marks it customer-safe.
//
// Editing any of the four text fields clears the embedding and asks kc-embed
// to rebuild it. The embedding is computed from that text, so a stale one
// would keep finding the nugget by its OLD meaning.
// ─────────────────────────────────────────────────────────────────────────────

const STATUSES = [
  { key: 'raw_candidate',   label: 'To Review' },
  { key: 'approved',        label: 'Approved' },
  { key: 'expert_verified', label: 'Expert Verified' },
  { key: 'rejected',        label: 'Rejected' },
  { key: 'auto_binned',     label: 'Auto-binned' },
  { key: 'all',             label: 'All' },
];

const STATUS_COLOR = {
  raw_candidate:   'var(--gold)',
  approved:        '#4ade80',
  expert_verified: '#38bdf8',
  rejected:        '#f87171',
  auto_binned:     'var(--muted)',
};

const TEXT_FIELDS = ['nugget', 'applies_when', 'reasoning', 'caveats'];

const CONVERSATION_LABEL = {
  staff_staff:       'staff → staff',
  staff_customer:    'staff → customer',
  customer_customer: 'customer → customer',
  bench:             'repair bench',
  shop_floor:        'shop floor (counter mic)',
  published:         'published page',
};
const convLabel = (t) => CONVERSATION_LABEL[t] || t || '—';
const SKILLS = ['beginner', 'intermediate', 'expert', 'any'];
const ASKED_BY = ['customer', 'staff', 'unknown'];

const inputStyle = {
  width: '100%', background: 'var(--bg)', border: '1px solid var(--border2)',
  borderRadius: 4, color: 'var(--white)', fontFamily: 'var(--font-body)',
  fontSize: 12, padding: '5px 8px', outline: 'none', boxSizing: 'border-box',
};

const Badge = ({ color, children, title }) => (
  <span title={title} style={{
    border: `1px solid ${color}`, color, borderRadius: 10,
    padding: '1px 8px', fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap',
  }}>
    {children}
  </span>
);

const Field = ({ label, children }) => (
  <div style={{ marginTop: 8 }}>
    <div style={{ fontSize: 10, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
      {label}
    </div>
    <div style={{ fontSize: 13, color: 'var(--white)', lineHeight: 1.5, marginTop: 2 }}>
      {children}
    </div>
  </div>
);

// ── Transcript context modal ─────────────────────────────────────────────────
// Shows the lines around a nugget's source_time so a reviewer can check what
// was actually said before approving it. A published source (a web page) has
// no timestamps — its row holds our summary notes, so show them all plus the link.
const TranscriptModal = ({ nugget, transcript, onClose }) => {
  if (!nugget) return null;
  const published = !!transcript?.source_url;
  const lines = (transcript?.transcript || '').split('\n');
  const at = nugget.source_time ? lines.findIndex(l => l.startsWith(`[${nugget.source_time}]`)) : -1;
  const from = published ? 0 : Math.max(0, (at < 0 ? 0 : at) - 6);
  const shown = published ? lines : lines.slice(from, from + 18);

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8,
        padding: 24, width: 760, maxWidth: '92vw', maxHeight: '80vh', overflowY: 'auto',
      }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--white)' }}>
          {transcript?.topic || 'Transcript'}
        </div>
        <div style={{ fontSize: 11, color: 'var(--muted)', margin: '4px 0 14px' }}>
          {transcript?.recorded_on} · {convLabel(transcript?.conversation_type)} ·{' '}
          {published
            ? <a href={transcript.source_url} target="_blank" rel="noreferrer"
                 style={{ color: 'var(--accent, #60a5fa)' }}>open source page</a>
            : <>around {nugget.source_time || '—'}
                {at < 0 && ' (exact line not found — showing the start)'}</>}
        </div>
        {shown.map((l, i) => (
          <div key={i} style={{
            fontSize: 12, lineHeight: 1.6, padding: '2px 6px', borderRadius: 3,
            color: 'var(--white)',
            background: from + i === at ? 'rgba(250,204,21,0.15)' : 'transparent',
          }}>
            {l}
          </div>
        ))}
        <div style={{ textAlign: 'right', marginTop: 14 }}>
          <button className="action-btn action-btn--ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

// ── One nugget ───────────────────────────────────────────────────────────────
const NuggetCard = ({ n, onSave, onShowSource, saving }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);

  const startEdit = () => {
    setDraft({
      ...Object.fromEntries(TEXT_FIELDS.map(f => [f, n[f] || ''])),
      sub_topic: n.sub_topic || '',
      customer_situation: n.customer_situation || '',
      skill_level: n.skill_level || 'any',
      products: (n.products || []).join(', '),
    });
    setEditing(true);
  };

  const saveEdit = async () => {
    const patch = {
      ...Object.fromEntries(TEXT_FIELDS.map(f => [f, draft[f].trim() || null])),
      sub_topic: draft.sub_topic.trim() || null,
      customer_situation: draft.customer_situation.trim() || null,
      skill_level: draft.skill_level,
      products: draft.products.split(',').map(s => s.trim()).filter(Boolean),
    };
    if (!patch.nugget) return;
    const textChanged = TEXT_FIELDS.some(f => (patch[f] || '') !== (n[f] || ''));
    const ok = await onSave(n.id, patch, textChanged);
    if (ok) setEditing(false);
  };

  const conf = (v) => (v === null || v === undefined ? '—' : Number(v).toFixed(2));

  return (
    <div style={{
      borderBottom: '1px solid var(--border)', padding: '14px 16px',
      borderLeft: `3px solid ${STATUS_COLOR[n.status] || 'var(--border)'}`,
    }}>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, color: 'var(--muted)', marginRight: 4 }}>#{n.id}</span>
        <Badge color={STATUS_COLOR[n.status]}>
          {STATUSES.find(s => s.key === n.status)?.label || n.status}
        </Badge>
        <Badge
          color={n.audience === 'customer_safe' ? '#4ade80' : '#f87171'}
          title={n.audience === 'customer_safe'
            ? 'Safe to tell customers'
            : 'Staff only — the agent must never say this to a customer'}
        >
          {n.audience === 'customer_safe' ? 'Customer-safe' : 'Internal only'}
        </Badge>
        {n.needs_visual && <Badge color="#a78bfa">Needs visual</Badge>}
        <span style={{ fontSize: 11, color: 'var(--muted)' }}>
          {[n.sub_topic, n.skill_level, n.customer_situation].filter(Boolean).join(' · ')}
        </span>
      </div>

      {editing ? (
        <div style={{ marginTop: 8 }}>
          {TEXT_FIELDS.map(f => (
            <Field key={f} label={f.replace('_', ' ')}>
              <textarea
                rows={f === 'nugget' || f === 'reasoning' ? 3 : 2}
                value={draft[f]}
                onChange={e => setDraft(d => ({ ...d, [f]: e.target.value }))}
                style={inputStyle}
              />
            </Field>
          ))}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 140px', gap: 10 }}>
            <Field label="sub topic">
              <input value={draft.sub_topic} style={inputStyle}
                     onChange={e => setDraft(d => ({ ...d, sub_topic: e.target.value }))} />
            </Field>
            <Field label="customer situation">
              <input value={draft.customer_situation} style={inputStyle}
                     onChange={e => setDraft(d => ({ ...d, customer_situation: e.target.value }))} />
            </Field>
            <Field label="skill level">
              <select value={draft.skill_level} style={inputStyle}
                      onChange={e => setDraft(d => ({ ...d, skill_level: e.target.value }))}>
                {SKILLS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
          </div>
          <Field label="products (comma separated)">
            <input value={draft.products} style={inputStyle}
                   onChange={e => setDraft(d => ({ ...d, products: e.target.value }))} />
          </Field>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button className="action-btn" onClick={saveEdit} disabled={saving || !draft.nugget.trim()}>
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
            <button className="action-btn action-btn--ghost" onClick={() => setEditing(false)} disabled={saving}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <>
          <div style={{ fontSize: 14, color: 'var(--white)', fontWeight: 600, lineHeight: 1.5, marginTop: 8 }}>
            {n.nugget}
          </div>
          {n.applies_when && <Field label="Applies when">{n.applies_when}</Field>}
          {n.reasoning    && <Field label="Why">{n.reasoning}</Field>}
          {n.caveats      && <Field label="Caveats">{n.caveats}</Field>}
          {n.products?.length > 0 && <Field label="Products">{n.products.join(', ')}</Field>}

          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 12 }}>
            {n.status !== 'approved' && (
              <button className="action-btn" disabled={saving}
                      onClick={() => onSave(n.id, { status: 'approved' })}>
                Approve
              </button>
            )}
            {n.status !== 'expert_verified' && (
              <button className="action-btn action-btn--ghost" disabled={saving}
                      onClick={() => onSave(n.id, { status: 'expert_verified' })}>
                Expert Verified
              </button>
            )}
            {n.status !== 'rejected' && (
              <button className="action-btn action-btn--ghost" disabled={saving}
                      style={{ color: '#f87171' }}
                      onClick={() => onSave(n.id, { status: 'rejected' })}>
                Reject
              </button>
            )}
            <button className="action-btn action-btn--ghost" disabled={saving}
                    onClick={() => onSave(n.id, {
                      audience: n.audience === 'customer_safe' ? 'internal' : 'customer_safe',
                    })}>
              {n.audience === 'customer_safe' ? 'Make Internal Only' : 'Make Customer-safe'}
            </button>
            <button className="action-btn action-btn--ghost" disabled={saving} onClick={startEdit}>
              Edit
            </button>
            <button className="action-btn action-btn--ghost" onClick={() => onShowSource(n)}>
              Show in Transcript
            </button>
            <span style={{ fontSize: 10, color: 'var(--muted)', marginLeft: 'auto' }}>
              {n.source_time && `at ${n.source_time} · `}
              {convLabel(n.conversation_type)} ·
              confidence {conf(n.extraction_confidence)} · transferability {conf(n.transferability)}
            </span>
          </div>
        </>
      )}
    </div>
  );
};

// ── Page ─────────────────────────────────────────────────────────────────────
const KnowledgeNuggets = () => {
  const [tab, setTab]                 = useState('nuggets');
  const [nuggets, setNuggets]         = useState([]);
  const [questions, setQuestions]     = useState([]);
  const [transcripts, setTranscripts] = useState({});
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [saving, setSaving]           = useState(false);
  const [notice, setNotice]           = useState(null);
  const [statusFilter, setStatusFilter]     = useState('raw_candidate');
  const [audienceFilter, setAudienceFilter] = useState('all');
  const [search, setSearch]           = useState('');
  const [sourceFor, setSourceFor]     = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const [n, q, t] = await Promise.all([
      supabase.from('kc_nuggets')
        .select('id, transcript_id, source_time, conversation_type, nugget, applies_when, reasoning, caveats, category, sub_topic, products, skill_level, customer_situation, audience, status, extraction_confidence, transferability, needs_visual, created_at')
        .order('id'),
      supabase.from('kc_questions')
        .select('id, transcript_id, source_time, question, asked_by')
        .order('id'),
      supabase.from('kc_transcripts')
        .select('id, conversation_type, recorded_on, topic')
        .order('id'),
    ]);
    const err = n.error || q.error || t.error;
    if (err) setError(err.message);
    setNuggets(n.data || []);
    setQuestions(q.data || []);
    setTranscripts(Object.fromEntries((t.data || []).map(r => [r.id, r])));
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Rebuild embeddings in small batches — kc-embed caps each call, and ~30 in
  // one call has hit the Edge Function's compute limit before.
  const reembed = async () => {
    for (let i = 0; i < 10; i++) {
      const { data, error: fnErr } = await supabase.functions.invoke('kc-embed', {
        body: { mode: 'pending_nuggets', limit: 5 },
      });
      if (fnErr || data?.error) return fnErr?.message || data.error;
      if (!data?.remaining) return null;
    }
    return null;
  };

  const save = async (id, patch, textChanged = false) => {
    setSaving(true);
    setNotice(null);
    const update = textChanged ? { ...patch, embedding: null } : patch;
    const { error: upErr } = await supabase.from('kc_nuggets').update(update).eq('id', id);
    if (upErr) {
      setSaving(false);
      setNotice({ bad: true, text: `Save failed: ${upErr.message}` });
      return false;
    }
    setNuggets(list => list.map(n => (n.id === id ? { ...n, ...patch } : n)));
    if (textChanged) {
      const embErr = await reembed();
      setNotice(embErr
        ? { bad: true, text: `Saved, but the search index wasn't rebuilt: ${embErr}` }
        : { bad: false, text: `#${id} saved and re-indexed for search.` });
    }
    setSaving(false);
    return true;
  };

  const saveQuestion = async (id, asked_by) => {
    const { error: upErr } = await supabase.from('kc_questions').update({ asked_by }).eq('id', id);
    if (upErr) { setNotice({ bad: true, text: `Save failed: ${upErr.message}` }); return; }
    setQuestions(list => list.map(q => (q.id === id ? { ...q, asked_by } : q)));
  };

  const showSource = async (n) => {
    if (!transcripts[n.transcript_id]?.transcript) {
      const { data } = await supabase.from('kc_transcripts')
        .select('id, conversation_type, recorded_on, topic, transcript, source_url')
        .eq('id', n.transcript_id).limit(1);
      if (data?.[0]) setTranscripts(t => ({ ...t, [data[0].id]: data[0] }));
    }
    setSourceFor(n);
  };

  const counts = useMemo(() => {
    const c = { all: nuggets.length };
    nuggets.forEach(n => { c[n.status] = (c[n.status] || 0) + 1; });
    return c;
  }, [nuggets]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return nuggets.filter(n =>
      (statusFilter === 'all' || n.status === statusFilter) &&
      (audienceFilter === 'all' || n.audience === audienceFilter) &&
      (!s || [n.nugget, n.applies_when, n.reasoning, n.caveats, n.sub_topic, (n.products || []).join(' ')]
              .some(v => (v || '').toLowerCase().includes(s)))
    );
  }, [nuggets, statusFilter, audienceFilter, search]);

  const filteredQuestions = useMemo(() => {
    const s = search.trim().toLowerCase();
    return questions.filter(q => !s || q.question.toLowerCase().includes(s));
  }, [questions, search]);

  return (
    <main className="app-main">
      <div style={{ padding: '24px 32px' }}>
        <div className="dash-panel">

          <div className="dash-panel__header">
            <span className="dash-panel__title">
              🧠 Knowledge Nuggets
              {counts.raw_candidate > 0 && (
                <span style={{
                  marginLeft: 8, background: 'var(--gold)', color: '#000',
                  borderRadius: 10, padding: '1px 7px', fontSize: 10, fontWeight: 700,
                }}>
                  {counts.raw_candidate} to review
                </span>
              )}
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                className={`action-btn ${tab === 'nuggets' ? 'action-btn--active' : 'action-btn--ghost'}`}
                style={{ fontSize: 10, padding: '2px 10px' }}
                onClick={() => setTab('nuggets')}
              >
                Nuggets ({nuggets.length})
              </button>
              <button
                className={`action-btn ${tab === 'questions' ? 'action-btn--active' : 'action-btn--ghost'}`}
                style={{ fontSize: 10, padding: '2px 10px' }}
                onClick={() => setTab('questions')}
              >
                Questions ({questions.length})
              </button>
              <button
                className="action-btn action-btn--ghost"
                style={{ fontSize: 10, padding: '2px 10px' }}
                onClick={load}
                disabled={loading}
              >
                ↻ Refresh
              </button>
            </div>
          </div>

          <div style={{
            padding: '10px 16px', borderBottom: '1px solid var(--border)',
            fontSize: 11, color: 'var(--muted)', lineHeight: 1.6,
          }}>
            {tab === 'nuggets' ? (
              <>
                Pieces of staff judgment pulled from recorded conversations. Only{' '}
                <strong style={{ color: 'var(--white)' }}>Approved</strong> and{' '}
                <strong style={{ color: 'var(--white)' }}>Expert Verified</strong> nuggets will be used
                by the advice agent, and only <strong style={{ color: 'var(--white)' }}>Customer-safe</strong>{' '}
                ones will ever be said to a customer. New nuggets start as Internal only.
              </>
            ) : (
              <>
                Every question heard in a recording, as it was actually asked. Customer questions become
                the test set for the agent; staff questions show how an expert narrows a situation down.
              </>
            )}
          </div>

          {notice && (
            <div style={{
              padding: '8px 16px', borderBottom: '1px solid var(--border)', fontSize: 11,
              color: notice.bad ? '#f87171' : '#4ade80',
            }}>
              {notice.text}
            </div>
          )}

          <div style={{
            display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap',
            padding: '10px 16px', borderBottom: '1px solid var(--border)',
          }}>
            {tab === 'nuggets' && STATUSES.map(s => (
              <button
                key={s.key}
                className={`action-btn ${statusFilter === s.key ? 'action-btn--active' : 'action-btn--ghost'}`}
                style={{ fontSize: 10, padding: '2px 10px' }}
                onClick={() => setStatusFilter(s.key)}
              >
                {s.label} ({counts[s.key] || 0})
              </button>
            ))}
            {tab === 'nuggets' && (
              <select
                value={audienceFilter}
                onChange={e => setAudienceFilter(e.target.value)}
                style={{ ...inputStyle, width: 'auto', fontSize: 11, padding: '3px 6px' }}
              >
                <option value="all">Any audience</option>
                <option value="customer_safe">Customer-safe</option>
                <option value="internal">Internal only</option>
              </select>
            )}
            <input
              type="text"
              placeholder={tab === 'nuggets' ? 'Search advice, topic or product…' : 'Search questions…'}
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ ...inputStyle, flex: 1, minWidth: 200, width: 'auto' }}
            />
          </div>

          {error ? (
            <div className="dash-empty" style={{ color: '#f87171' }}>Could not load: {error}</div>
          ) : loading ? (
            <div className="dash-loading">Loading…</div>
          ) : tab === 'nuggets' ? (
            !filtered.length ? (
              <div className="dash-empty dash-empty--ok">
                {nuggets.length ? '✓ Nothing matches this filter' : 'No nuggets yet'}
              </div>
            ) : (
              filtered.map(n => (
                <NuggetCard key={n.id} n={n} onSave={save} onShowSource={showSource} saving={saving} />
              ))
            )
          ) : (
            !filteredQuestions.length ? (
              <div className="dash-empty">No questions</div>
            ) : (
              <table className="dash-table">
                <thead>
                  <tr>
                    <th style={{ width: 80 }}>At</th>
                    <th>Question</th>
                    <th style={{ width: 140 }}>Asked by</th>
                    <th style={{ width: 200 }}>Conversation</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredQuestions.map(q => (
                    <tr key={q.id}>
                      <td style={{ color: 'var(--muted)' }}>{q.source_time || '—'}</td>
                      <td>{q.question}</td>
                      <td>
                        <select
                          value={q.asked_by}
                          onChange={e => saveQuestion(q.id, e.target.value)}
                          style={{ ...inputStyle, fontSize: 11, padding: '2px 6px' }}
                        >
                          {ASKED_BY.map(a => <option key={a} value={a}>{a}</option>)}
                        </select>
                      </td>
                      <td style={{ color: 'var(--muted)', fontSize: 11 }}>
                        {transcripts[q.transcript_id]?.topic || `#${q.transcript_id}`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          )}
        </div>
      </div>

      <TranscriptModal
        nugget={sourceFor}
        transcript={sourceFor ? transcripts[sourceFor.transcript_id] : null}
        onClose={() => setSourceFor(null)}
      />
    </main>
  );
};

export default KnowledgeNuggets;
